import { useState, useEffect } from 'react';
import { Box, Drawer, CssBaseline } from '@mui/material';
import Header from '../components/Header';
import LeftNav from '../components/LeftNav';
import TopControls from '../components/TopControls';
import ChatDisplay from '../components/ChatDisplay';
import ChatInputBar from '../components/ChatInputBar';
import { fetchCustomers, postChatMessage } from '../services/apiClient';
import { getHotTopics, getCalendarData, getFaqs  } from '../services/adminApiClient';
import { v4 as uuidv4 } from 'uuid';

const drawerWidth = 360;

export default function CopilotPage({ onLogout, initialCustomerName, initialCountryCode, initialContactId }) {
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(true);
    const [customersError, setCustomersError] = useState(null);

    const [selectedClientCode, setSelectedClientCode] = useState('');
    const [countryCode, setCountryCode] = useState(initialCountryCode || 'US');
    const [languageCode, setLanguageCode] = useState('en'); // default to English
    const [conversationId, setConversationId] = useState(() => `conv_${uuidv4()}`); // unique UUID, resettable

    const [chatHistory, setChatHistory] = useState([]);
    const [sources, setSources] = useState([]);
    const [logId, setLogId] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    
    const [allHotTopics, setAllHotTopics] = useState([]);
    const [hotTopicsError, setHotTopicsError] = useState(null);
    const [allCalendars, setAllCalendars] = useState([]);
    const [calendarsError, setCalendarsError] = useState(null);

    const [lastResponseTime, setLastResponseTime] = useState(null);

    const [allFaqs, setAllFaqs] = useState([]);
    const [faqsError, setFaqsError] = useState(null);



    const [contextParams, setContextParams] = useState({
        countryCode: initialCountryCode || 'US',
        contactId: initialContactId || 'default_user'
    });

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const customerData = await fetchCustomers();
                setCustomers(customerData);

                const countryFiltered = customerData.filter(
                    c => c.region === (initialCountryCode || 'US')
                );

                if (initialCustomerName) {
                    const matchedCustomer = countryFiltered.find(
                        c => c.ClientCode.toLowerCase() === initialCustomerName.toLowerCase()
                    );
                    setSelectedClientCode(
                        matchedCustomer ? matchedCustomer.ClientCode : (countryFiltered[0]?.ClientCode || '')
                    );
                } else {
                    setSelectedClientCode(countryFiltered[0]?.ClientCode || '');
                }
            } catch (e) {
                setCustomersError(e.message);
            } finally {
                setCustomersLoading(false);
            }
            
            try {
                const hotTopicsResponse = await getHotTopics(true);
                if (hotTopicsResponse.success) {
                    setAllHotTopics(hotTopicsResponse.data);
                } else {
                    throw new Error("Invalid data format for hot topics.");
                }
            } catch (e) {
                setHotTopicsError(e.message);
            }

            try {
                const calendarResponse = await getCalendarData();
                if (calendarResponse.success) {
                    setAllCalendars(calendarResponse.data);
                } else {
                    throw new Error("Invalid data format for calendars.");
                }
            } catch (e) {
                setCalendarsError(e.message);
            }

            try {
                const faqResponse = await getFaqs();
                if (faqResponse.success) {
                    setAllFaqs(faqResponse.data);
                } else {
                    throw new Error("Invalid data format for FAQs.");
                }
            } catch (e) {
                setFaqsError(e.message);
            }

        };

        loadInitialData();
    }, [initialCustomerName, initialCountryCode, initialContactId]);

    const handleClientChange = (clientCode) => {
        setSelectedClientCode(clientCode);
        setChatHistory([]);
        setSources([]);
        setConversationId(`conv_${uuidv4()}`); // new UUID session when client changes
    };

    const handleCountryChange = (newCountry) => {
        setCountryCode(newCountry);
        setContextParams(prev => ({ ...prev, countryCode: newCountry }));

        const filtered = customers.filter(c => c.region === newCountry);
        setSelectedClientCode(filtered[0]?.ClientCode || '');
        setChatHistory([]);
        setSources([]);
    };

    const handleAsk = async (query) => {
        if (!query.trim() || !selectedClientCode) return;
        setIsChatLoading(true);
        const newChatHistory = [...chatHistory, { role: 'user', content: query }];
        // setChatHistory(newChatHistory);
        setSources([]);
        try {
            const startTime = Date.now(); // ⏱ start timer
            const response = await postChatMessage(
                selectedClientCode, 
                query,
                chatHistory,
                conversationId,
                languageCode
            );
            const endTime = Date.now(); // ⏱ end timer
            setLastResponseTime(endTime - startTime); // milliseconds

            setChatHistory([...newChatHistory, { role: 'assistant', content: response.answer }]);
            setSources(response.sources || []);
            setLogId(response.log_id);
        } catch (e) {
            setChatHistory([...newChatHistory, { role: 'assistant', content: `Error: ${e.message}`, error: true }]);
        } finally {
            setIsChatLoading(false);
        }
    };


    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Drawer variant="permanent" sx={{ width: drawerWidth, flexShrink: 0, [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', p: 2 } }}>
                <LeftNav 
                    sources={sources} 
                    allHotTopics={allHotTopics}
                    hotTopicsError={hotTopicsError}
                    allCalendars={allCalendars}
                    calendarsError={calendarsError}
                    selectedClientCode={selectedClientCode}
                    allFaqs={allFaqs}
                    faqsError={faqsError}
                    onAskFaq={(faqContent) => handleAsk(faqContent)}
                />
            </Drawer>
            
            <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header onLogout={onLogout} /> 
                <TopControls 
                    clients={customers.filter(c => c.region === countryCode)} 
                    selectedClient={selectedClientCode} 
                    onClientChange={handleClientChange}
                    countryCode={countryCode}
                    onCountryChange={handleCountryChange}
                    languageCode={languageCode}
                    onLanguageChange={setLanguageCode}
                    loading={customersLoading}
                    error={customersError}
                />
                <ChatDisplay 
                    chatHistory={chatHistory} 
                    isLoading={isChatLoading}
                    clientCode={selectedClientCode}
                    countryCode={countryCode}
                    contactId={contextParams.contactId}
                    conversationId={conversationId}
                    responseTime={lastResponseTime}
                    logId={logId}
                />
                <ChatInputBar 
                    onAsk={handleAsk} 
                    disabled={isChatLoading} 
                    onClear={() => { 
                        setChatHistory([]); 
                        setSources([]); 
                        setConversationId(`conv_${uuidv4()}`); // reset session on clear
                    }} 
                />
            </Box>
        </Box>
    );
}
